/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * SeoSection — Accordion 7: meta/OG fields + social preview card.
 */

import React, { useEffect, useRef } from 'react';

/** Strip leading # and split a comma-separated tag string into an array. */
function parseTags(str) {
  if (!str || typeof str !== 'string') return [];
  return str.split(',').map(t => t.trim().replace(/^#/, '')).filter(Boolean);
}

// Roles that can be sourced from a table field instead of typed manually,
// mirroring FieldMappingSection's role → field dropdown pattern.
const SEO_FIELD_CONFIGS = [
  { key: 'meta_title', label: 'Meta Title', kind: 'input', maxLength: 60,
    placeholder: 'Page title shown in browser tab and search results' },
  { key: 'meta_description', label: 'Meta Description', kind: 'textarea', maxLength: 160,
    placeholder: 'Brief description shown in search engine snippets' },
  { key: 'og_title', label: 'OG Title (Social Share)', kind: 'input', maxLength: 90,
    placeholder: 'Defaults to Meta Title' },
  { key: 'og_description', label: 'OG Description (Social Share)', kind: 'textarea', maxLength: 200,
    placeholder: 'Defaults to Meta Description' },
  { key: 'og_image_url', label: 'OG Image URL', kind: 'input',
    placeholder: 'https://…/social-image.jpg (1200×630 recommended)' },
];

export default function SeoSection({ config, updateConfig, fields = [], theme }) {
  const seo = config.seo || {};
  const branding = config.branding || {};

  // Auto-fill meta_title from page_title and meta_description from subtitle
  // when the SEO fields are still empty (user hasn't typed in them yet)
  const prevBrandingRef = useRef({});
  useEffect(() => {
    const prev = prevBrandingRef.current;
    const currentSeo = config.seo || {};
    const patch = {};

    if (
      branding.page_title &&
      branding.page_title !== prev.page_title &&
      !currentSeo.meta_title
    ) {
      patch.meta_title = branding.page_title;
    }
    if (
      branding.subtitle &&
      branding.subtitle !== prev.subtitle &&
      !currentSeo.meta_description
    ) {
      patch.meta_description = branding.subtitle;
    }

    if (Object.keys(patch).length) {
      updateConfig({ seo: { ...currentSeo, ...patch } });
    }
    prevBrandingRef.current = { page_title: branding.page_title, subtitle: branding.subtitle };
  }, [branding.page_title, branding.subtitle]); // eslint-disable-line react-hooks/exhaustive-deps

  function update(key, value) {
    updateConfig({ seo: { ...seo, [key]: value } });
  }

  function updateSource(key, fieldName) {
    updateConfig({ seo: { ...seo, [`${key}_field`]: fieldName || null } });
  }

  const previewTitle = seo.og_title || seo.meta_title || 'Page Title';
  const previewDesc = seo.og_description || seo.meta_description || 'Page description goes here.';

  return (
    <div className="d-flex flex-column gap-3">
      {SEO_FIELD_CONFIGS.map(cfg => {
        const sourceField = seo[`${cfg.key}_field`] || '';
        const sourcedFieldObj = sourceField ? fields.find(f => f.name === sourceField) : null;
        const inputId = `seo-${cfg.key}`;
        return (
          <div key={cfg.key}>
            <div className="d-flex align-items-center justify-content-between mb-1 gap-2">
              <label className="form-label small fw-semibold mb-0" htmlFor={inputId}>
                {cfg.label}
              </label>
              <select
                aria-label={`${cfg.label} source`}
                className="form-select form-select-sm"
                style={{ width: 'auto', fontSize: 12 }}
                value={sourceField}
                onChange={e => updateSource(cfg.key, e.target.value)}
              >
                <option value="">Custom text</option>
                {fields.map(f => (
                  <option key={f.name} value={f.name}>
                    {f.display_name || f.name}
                  </option>
                ))}
              </select>
            </div>

            {/* The manual input used to be replaced by this note once a field
                was chosen, which read as the typed text being erased. It never
                was: the list page has no record to source from and keeps using
                it, so it stays editable here.
            {sourceField ? (
              <div className="form-text">
                Sourced from this record's "{sourcedFieldObj?.display_name || sourceField}" field at runtime.
              </div>
            ) : cfg.kind === 'textarea' ? (
            */}
            {sourceField && (
              <div className="form-text mt-0 mb-1">
                Detail pages are sourced from each record's "{sourcedFieldObj?.display_name || sourceField}" field.
                The text below is used on the list page.
              </div>
            )}
            {cfg.kind === 'textarea' ? (
              <textarea
                id={inputId}
                className="form-control form-control-sm"
                rows={2}
                value={seo[cfg.key] || ''}
                onChange={e => update(cfg.key, e.target.value)}
                placeholder={cfg.placeholder}
                maxLength={cfg.maxLength}
              />
            ) : (
              <input
                id={inputId}
                type="text"
                className="form-control form-control-sm"
                value={seo[cfg.key] || ''}
                onChange={e => update(cfg.key, e.target.value)}
                placeholder={cfg.placeholder}
                maxLength={cfg.maxLength}
              />
            )}
            {/* {!sourceField && cfg.maxLength && ( */}
            {cfg.maxLength && (
              <div className="form-text">{(seo[cfg.key] || '').length}/{cfg.maxLength} characters</div>
            )}
          </div>
        );
      })}

      {/* Social preview card */}
      <div>
        <div className="small text-muted mb-2 fw-semibold">Social Preview</div>
        <div
          className="border rounded overflow-hidden"
          style={{ maxWidth: 480, background: '#fff' }}
        >
          {seo.og_image_url && (
            <img
              src={seo.og_image_url}
              alt="OG Preview"
              style={{ width: '100%', height: 120, objectFit: 'cover' }}
              onError={e => { e.target.style.display = 'none'; }}
            />
          )}
          <div className="p-2" style={{ borderTop: '4px solid #e2e8f0' }}>
            <div
              className="fw-bold"
              style={{ fontSize: 14, color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {previewTitle}
            </div>
            <div className="text-muted" style={{ fontSize: 12, marginTop: 2 }}>{previewDesc}</div>
          </div>
        </div>
      </div>

      {/* ── Social Hashtags ── */}
      <div>
        <label className="form-label small fw-semibold"># Social Hashtags</label>
        <input
          type="text"
          className="form-control form-control-sm"
          value={seo.hashtags || ''}
          onChange={e => update('hashtags', e.target.value)}
          placeholder="e.g. travel, food, adventure"
        />
        <div className="form-text">
          Comma-separated tags without #. Stored for use across Twitter, Facebook, and Instagram.
        </div>
        {parseTags(seo.hashtags).length > 0 && (
          <div className="d-flex flex-wrap gap-1 mt-1">
            {parseTags(seo.hashtags).map(tag => (
              <span
                key={tag}
                style={{
                  fontSize: '0.75rem',
                  padding: '2px 8px',
                  borderRadius: 20,
                  background: '#6366f118',
                  color: '#6366f1',
                  border: '1px solid #6366f140',
                  fontWeight: 500,
                }}
              >
                #{tag}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
